// pages/PlatformUserManagement/components/PlatformUserFormModal.jsx
// Matches memberFormModal pattern exactly:
//   - BaseModal wrapper
//   - fetchWrapper for submit
//   - showMainToast for feedback
//   - phone as login ID (not email)
//   - name as single field

import React, { useState, useEffect } from "react";
import BaseModal from "components/BaseModal";
import { showMainToast } from "utils/showToast";
import { getAllowedModules,PLATFORM_MODULES } from "./PlatFormConfig";
import { createPlatformUser,updatePlatformUser } from "./PlatformUserAction";

const INIT = {
  name:            "",
  email:           "",
  phone:           "",
  password:        "",
  can_report:      false,
  can_credit:      false,
  can_debit:       false,
  can_member_view: false,
  can_member_edit: false,
  notes:           "",
};

export default function PlatformUserFormModal({ show, onClose, mode = "add", userData, onSuccess }) {
  const [form,    setForm]    = useState(INIT);
  const [errors,  setErrors]  = useState({});
  const [loading, setLoading] = useState(false);
  const [showPwd, setShowPwd] = useState(false);

  const role           = localStorage.getItem("role") || "";
  const allowedModules = getAllowedModules(role);

  useEffect(() => {
    if (!show) return;
    if (mode === "edit" && userData) {
      setForm({
        name:            userData.name            || "",
        email:           userData.email           || "",
        phone:           userData.phone           || "",
        password:        "",
        can_report:      !!userData.can_report,
        can_credit:      !!userData.can_credit,
        can_debit:       !!userData.can_debit,
        can_member_view: !!userData.can_member_view,
        can_member_edit: !!userData.can_member_edit,
        notes:           userData.notes           || "",
      });
    } else {
      setForm(INIT);
    }
    setErrors({});
    setShowPwd(false);
  }, [show, mode, userData]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((p) => ({ ...p, [name]: type === "checkbox" ? checked : value }));
    if (errors[name]) setErrors((p) => ({ ...p, [name]: "" }));
  };

  const validate = () => {
    const errs = {};
    if (!form.name.trim())  errs.name  = "Name is required.";
    if (!form.phone.trim()) errs.phone = "Phone is required.";
    else if (!/^\d{10}$/.test(form.phone.trim())) errs.phone = "Enter a valid 10-digit phone.";
    if (mode === "add" && form.password && form.password.length < 8)
      errs.password = "Password must be at least 8 characters.";
    return errs;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const errs = validate();
    if (Object.keys(errs).length) { setErrors(errs); return; }

    setLoading(true);
    try {
      const payload = {
        name:            form.name.trim(),
        email:           form.email.trim(),
        phone:           form.phone.trim(),
        can_report:      String(form.can_report),
        can_credit:      String(form.can_credit),
        can_debit:       String(form.can_debit),
        can_member_view: String(form.can_member_view),
        can_member_edit: String(form.can_member_edit),
        notes:           form.notes,
      };
      if (form.password) payload.password = form.password;

      const res = mode === "add"
        ? await createPlatformUser(payload)
        : await updatePlatformUser(userData.id, payload);

      if (res?.status) {
        showMainToast(res.data || (mode === "add" ? "Platform user created." : "Updated successfully."), "success");
        onSuccess?.();
        onClose?.();
      } else {
        showMainToast(res?.data || "Something went wrong.", "error");
      }
    } catch {
      showMainToast("Server error. Please try again.", "error");
    } finally {
      setLoading(false);
    }
  };

  return (
    <BaseModal
      show={show}
      onClose={onClose}
      title={mode === "add" ? "Create Platform User" : "Edit Platform User"}
      icon="ph-duotone ph-user-gear"
      size="lg"
    >
      <form onSubmit={handleSubmit} noValidate>

        {/* Info banner */}
        <div className="alert alert-primary d-flex align-items-center gap-2 py-2 mb-3" style={{ fontSize: 13 }}>
          <i className="ph-duotone ph-info" />
          <span>
            <strong>Phone</strong> is the login ID.
            {mode === "add" && " Leave password blank to auto-generate and send via SMS."}
          </span>
        </div>

        {/* ── Personal Details ──────────────────────────────────── */}
        <div className="row g-3 mb-3">
          <div className="col-12">
            <p className="f-w-600 text-muted mb-1" style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: ".06em" }}>
              Personal Details
            </p>
          </div>

          <div className="col-sm-6">
            <label className="form-label">Full Name <span className="text-danger">*</span></label>
            <input
              className={`form-control ${errors.name ? "is-invalid" : ""}`}
              name="name"
              value={form.name}
              onChange={handleChange}
              placeholder="Full name"
            />
            {errors.name && <div className="invalid-feedback">{errors.name}</div>}
          </div>

          <div className="col-sm-6">
            <label className="form-label">
              Phone <span className="text-danger">*</span>
              <span className="text-muted ms-1" style={{ fontSize: 11 }}>(Login ID)</span>
            </label>
            <input
              className={`form-control ${errors.phone ? "is-invalid" : ""}`}
              name="phone"
              value={form.phone}
              onChange={handleChange}
              placeholder="10-digit mobile"
              maxLength={10}
              disabled={mode === "edit"}
            />
            {errors.phone && <div className="invalid-feedback">{errors.phone}</div>}
          </div>

          <div className="col-sm-6">
            <label className="form-label">Email <span className="text-muted">(optional)</span></label>
            <input
              className="form-control"
              name="email"
              type="email"
              value={form.email}
              onChange={handleChange}
              placeholder="email@example.com"
            />
          </div>

          {mode === "add" && (
            <div className="col-sm-6">
              <label className="form-label">Password <span className="text-muted">(optional)</span></label>
              <div className="input-group">
                <input
                  className={`form-control ${errors.password ? "is-invalid" : ""}`}
                  name="password"
                  type={showPwd ? "text" : "password"}
                  value={form.password}
                  onChange={handleChange}
                  placeholder="Leave blank to auto-generate"
                />
                <button
                  type="button"
                  className="btn btn-outline-secondary"
                  onClick={() => setShowPwd((p) => !p)}
                >
                  <i className={`ph-duotone ${showPwd ? "ph-eye-slash" : "ph-eye"}`} />
                </button>
                {errors.password && <div className="invalid-feedback">{errors.password}</div>}
              </div>
            </div>
          )}
        </div>

        {/* ── Module Permissions ─────────────────────────────────── */}
        <div className="row g-2 mb-3">
          <div className="col-12">
            <p className="f-w-600 text-muted mb-1" style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: ".06em" }}>
              Module Permissions
            </p>
          </div>

          {allowedModules.map((mod) => (
            <div className="col-sm-6" key={mod.key}>
              <label
                className={`d-flex align-items-start gap-2 p-3 rounded border w-100 ${form[mod.key] ? "border-primary bg-light" : "border"}`}
                style={{ cursor: "pointer", transition: "all .15s", minHeight: 64 }}
              >
                <input
                  type="checkbox"
                  className="form-check-input mt-0 flex-shrink-0"
                  name={mod.key}
                  checked={!!form[mod.key]}
                  onChange={handleChange}
                />
                <div>
                  <div className="f-w-500" style={{ fontSize: 13 }}>
                    <i className={`${mod.icon} me-1`} />
                    {mod.label}
                  </div>
                  <div className="text-muted" style={{ fontSize: 11 }}>{mod.description}</div>
                </div>
              </label>
            </div>
          ))}

          {allowedModules.length === 0 && (
            <div className="col-12">
              <div className="alert alert-warning py-2" style={{ fontSize: 13 }}>
                <i className="ph-duotone ph-warning me-1" />
                Your role has no grantable modules.
              </div>
            </div>
          )}
        </div>

        {/* ── Notes ─────────────────────────────────────────────── */}
        <div className="mb-3">
          <label className="form-label">Notes <span className="text-muted">(optional)</span></label>
          <textarea
            className="form-control"
            name="notes"
            value={form.notes}
            onChange={handleChange}
            rows={2}
            placeholder="Internal notes about this platform user"
          />
        </div>

        {/* ── Footer buttons ─────────────────────────────────────── */}
        <div className="d-flex justify-content-end gap-2 pt-1">
          <button type="button" className="btn btn-secondary" onClick={onClose} disabled={loading}>
            Cancel
          </button>
          <button type="submit" className="btn btn-primary" disabled={loading}>
            {loading ? (
              <><span className="spinner-border spinner-border-sm me-2" />
              {mode === "add" ? "Creating..." : "Saving..."}</>
            ) : (
              <><i className={`ph-duotone ${mode === "add" ? "ph-user-plus" : "ph-floppy-disk"} me-1`} />
              {mode === "add" ? "Create & Send Credentials" : "Save Changes"}</>
            )}
          </button>
        </div>

      </form>
    </BaseModal>
  );
}